// delivery.service.ts
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable, switchMap, of, from } from 'rxjs';
import { BaseApiService } from './base-api-service';
import { BusyService } from './busy-service';
import { FilteredLoad } from './load-management.service';
import { LastPriceRow } from './customer-favorites-service';
import { StorageService } from './storage';

/** ===================== Common Types ===================== */

export type LoadState = 'draft' | 'loaded' | 'in_transit' | 'ready_settlement' | 'settled';
export type UpdatableLoadState = 'in_transit' | 'ready_settlement'; // allowed by API
export type DeliveryLoad = FilteredLoad & { state: LoadState };

/** ===================== Request payloads ===================== */

export interface AssignedLoadsRequestPayload {
  startDate?: string; // 'YYYY-MM-DD' or ISO (backend reads as create_date range)
  endDate?: string;
  page?: number;
  limit?: number;
}

export interface LoadDetailsRequestPayload {
  load_id: number;
}

/** ===================== Responses: /delivery/loads ===================== */

export interface AssignedLoadsResponse {
  status: 'success' | 'error';
  message?: string;
  page: number;
  page_size: number;
  total: number;
  pages: number;
  state: LoadState;
  loads: DeliveryLoad[]; // includes `state`
}

/** ===================== Models: /delivery/get ===================== */

export interface InvoiceReportItem {
  customerId: number;
  customerName: string | null;
  salesOrder: string | null;
  invoiceId: number;
  invoiceNumber: string | null;
  invoicedAmount: number;
  balanceExcludingThisLoad: number;
  balanceExcludingThisInvoice: number;
  totalOutstandingForCustomer: number;
  delivered: boolean;
  paymentState: 'paid' |'reversed' | 'not_paid' | 'in_payment' | 'paid' | 'partial';
  // now available from backend:
  deliveredOn?: string | null;   // 'YYYY-MM-DD HH:mm:ss'
  deliveredBy?: string | null;
}

// ADD this interface near the other models
export interface DeliveredStats {
  total: number;
  delivered: number;
  allDelivered: boolean;
}

export interface LoadDetails {
  id: number;
  name: string;
  state: LoadState;
  deliveryBoyId: number | null;
  deliveryBoyName: string | null;
  salesManId: number | null;
  salesManName: string | null;
  note?: string
  write_date: string | null;   // 'YYYY-MM-DD HH:mm:ss'
  createdAt: string | null;    // 'YYYY-MM-DD HH:mm:ss'
  invoicesReport: InvoiceReportItem[];
  deliveredStats?: DeliveredStats;   // ← NEW
}

export interface LoadDetailsResponse {
  status: 'success' | 'error';
  message?: string;
  load?: LoadDetails;
}

/** ===================== Models: /invoice/get ===================== */

export interface InvoiceTotals {
  amountUntaxed: number;
  amountTax: number;
  amountTotal: number;
  amountResidual: number;
  totalCredits?: number;
}

export interface InvoiceLine {
  id: number;
  name: string | null;
  productId: number | null;
  productName: string | null;
  quantity: number;
  priceUnit: number;
  discount: number;
  priceSubtotal: number; // tax-excluded
  priceTotal: number;    // tax-included
  uom: string | null;
  taxes: Array<{ id: number; name: string; amount: number }>;
}

export interface InvoicePaymentItem {
  paymentId: number | null;  // payment move id
  name: string | null;
  date: string | null;       // 'YYYY-MM-DD'
  journal: string | null;
  amount: number;
  currency: string | null;
  ref: string | null;
  moveName: string | null;
}

/** ===================== Models: /invoice/pending ===================== */

export interface PendingInvoicesRequest {
  partner_id: number;
  commercial?: boolean;          // default true (server)
  overdue_only?: boolean;        // default false
  invoice_date_from?: string;    // 'YYYY-MM-DD'
  invoice_date_to?: string;      // 'YYYY-MM-DD'
  company_id?: number;
  page?: number;
  limit?: number;
}

export interface PendingInvoicesPartner {
  id: number;
  name: string;
  commercialPartnerId: number | null;
  commercialPartnerName: string | null;
}

export interface PendingInvoicesSummary {
  currency: string | null;
  asOf: string;                  // 'YYYY-MM-DD'
  openTotal: number;
  overdueTotal: number;
  notOverdueTotal: number;
}

export interface PendingInvoiceListItem {
  invoiceId: number;
  invoiceNumber: string | null;
  invoiceDate: string | null;    // 'YYYY-MM-DD'
  invoiceDueDate: string | null; // 'YYYY-MM-DD'
  amountTotal: number;
  amountResidual: number;
  paymentState: 'not_paid' | 'in_payment' | 'paid' | 'partial';
  journal: string | null;
  currency: string | null;
  salesOrder: string | null;
  isOverdue: boolean;
  daysOverdue: number;
}

export interface PendingInvoicesResponse {
  status: 'success' | 'error';
  message?: string;
  page: number;
  page_size: number;
  total: number;
  pages: number;
  partner: PendingInvoicesPartner;
  summary: PendingInvoicesSummary;
  invoices: PendingInvoiceListItem[];
}

export interface InvoiceDetails {
  // header
  id: number;
  name: string | null; 
  salesOrder: string | null; // Sales Order Number
  moveType: 'out_invoice';
  state: 'draft' | 'posted' | 'cancel';
  paymentState: 'paid' |'reversed' | 'not_paid' | 'in_payment' | 'paid' | 'partial';
  invoiceDate: string | null;         // 'YYYY-MM-DD'
  invoiceDueDate: string | null;      // 'YYYY-MM-DD'
  ref: string | null;
  journal: string | null;
  company: string | null;
  currency: string | null;
  partnerId: number | null;
  partnerName: string | null;
  commercialPartnerId: number | null;
  commercialPartnerName: string | null;
  salesperson: string | null;

  // groups
  totals: InvoiceTotals;
  lines: InvoiceLine[];
  payments: InvoicePaymentItem[];

  // convenience flags
  isPosted: boolean;
  isPaid: boolean;
  isPartial: boolean;
  isInPayment: boolean;
}

export interface InvoiceGetResponse {
  status: 'success' | 'error';
  message?: string;
  invoice?: InvoiceDetails;
}

/** ===================== Models: /customer/last_invoice ===================== */

export interface CustomerLastInvoiceLine {
  id: number;
  productId: number | null;
  productName: string | null;
  quantity: number;
  priceUnit: number;
  discount: number;
  priceSubtotal: number;
  priceTotal: number;
  uom: string | null;
}

export interface CustomerLastInvoice {
  id: number;
  name: string | null;
  invoiceDate: string | null;
  invoiceDueDate: string | null;
  amountTotal: number;
  amountResidual: number;
  currency: string | null;
  partnerId: number | null;
  partnerName: string | null;
  salesOrder: string | null;
  lines: CustomerLastInvoiceLine[];
}

export interface CustomerLastInvoiceResponse {
  status: 'success' | 'error';
  message?: string;
  invoice?: CustomerLastInvoice | null;
}

/** ===================== Requests: payments & returns ===================== */

export interface PaymentRequest {
  invoice_id: number;
  amount: number;
  invoice_discount: number;
  method: 'cash' | 'cheque' | 'upi' | 'bank' | 'other';
  reference?: string;
  note?: string;
  load_id?: number;
  credit_note_ids?: number[];
}

export interface ReturnLineRequest {
  quantity: number;
  price_unit?: number;
  reason: string;
}

export type ReturnMode = 'full' | 'partial' | 'amount';

export interface ReturnRequest {
  invoice_id: number;
  mode: ReturnMode;
  lines?: ReturnLineRequest[];
  amount: number;
  reason: string;
}

export interface PaymentAndReturnRequest extends PaymentRequest, ReturnRequest { }

/** ===================== Responses: payments & returns ===================== */

export interface PaymentResponse {
  payment: {
    id: number;
    name: string;
    amount: number;
    state: string;
    journal: string;
    paymentDate: string;
    partnerId: number;
    partnerName: string;
    method: string;
  };
  collection: {
    id: number;
    invoiceId: number;
    amount: number;
    method: string;
    date: string;
    deliveryBoyId: number;
  };
}

export interface ReturnResponse {
  return: {
    id: number;
    name: string;
    state: string;
    amountTotal: number;
    currency: string;
    invoiceId: number;
    invoiceNumber: string;
  };
}

/** ===================== Return WITHOUT invoice ===================== */

export interface ReturnWithoutInvoiceLine {
  product_id: number;
  quantity: number;
  price_unit: number;
  tax_ids?: number[];
  discount?: number;
  uom_id?: number;
  reason?: string;
}

export interface StandaloneReturnRequest {
  partner_id: number;
  reason: string;
  lines?: ReturnWithoutInvoiceLine[]; // optional; amount-only mode allowed
  amount?: number;                    // used when no lines (recorded as amount_adjust)
  // optional context
  invoiceId?: number;
  load_id?: number;
  delivery_boy_id?: number;
  journal_id?: number;
}

export interface DeliveryReturnSummary {
  id: number;
  name: string;
  date: string;            // 'YYYY-MM-DD HH:mm:ss'
  amountTotal: number;
  qtyTotal: number;
  amountAdjust: number;
  invoiceId: number;
  loadId: number | null;
  deliveryBoyId: number;
}

export interface ReturnWithoutInvoiceResponse {
  return: {
    id: number;
    name: string;
    state: string;
    amountTotal: number;
    currency: string;
    partnerId: number;
    partnerName: string;
    reason: string;
  };
  deliveryReturn?: DeliveryReturnSummary; // present when backend mirrors to a1.delivery.return
}

/** ===================== Customer balance ===================== */

export interface CustomerBalanceRequest {
  customerId: number;
}

export interface CustomerBalanceResponse {
  status: 'success' | 'error';
  message?: string;
  balance?: {
    partnerId: number;
    partnerName: string;
    totalReceivable: number;
    totalPayable: number;
    netBalance: number;
    currency: string;
  };
}

export interface PaymentAndReturnResponse {
  return: ReturnResponse['return'];
  payment: PaymentResponse['payment'];
  collection: PaymentResponse['collection'];
}

/** ===================== Models: /customer/credits ===================== */

export interface CustomerCreditsRequest {
  customer_id: number;
  only_open?: boolean;   // default true on server
  q?: string;            // search
  page?: number;         // default 1
  limit?: number;        // default 50
}

export interface CustomerCreditItem {
  id: number;
  name: string | null;
  date: string | null;         // 'YYYY-MM-DD'
  journal: string | null;
  ref: string | null;
  origin: string | null;
  currency: string | null;
  amountTotal: number;
  openAmount: number;          // remaining credit (company currency)
  linesCount: number;
}

export interface CustomerCreditsPartner {
  id: number;
  name: string;
  commercialPartnerId: number;
  commercialPartnerName: string;
}

export interface CustomerCreditsResponse {
  status: 'success' | 'error';
  message?: string;
  page: number;
  page_size: number;
  pages: number;
  total: number;
  credits: CustomerCreditItem[];
  partner: CustomerCreditsPartner;
}

/** ===================== Models: /customer/returns ===================== */

export interface CustomerReturnsRequest {
  customerId: number;          // required
  loadId: number;              // optional
}

export interface CustomerReturnLine {
  id: number;
  productId: number | null;
  productName: string | null;
  uomId: number | null;
  uomName: string | null;
  quantity: number;
  priceUnit: number;
  discount: number;
  subtotal: number;
  reason: string | null;
}

export interface CustomerReturnHeader {
  id: number;
  name: string;
  date: string | null;           // 'YYYY-MM-DD HH:mm:ss'
  state: 'draft' | 'confirmed';
  amountTotal: number;
  qtyTotal: number;
  amountAdjust: number;
  invoiceId: number | null;
  invoiceNumber: string | null;
  loadId: number | null;
  deliveryBoyId: number | null;
  deliveryBoyName: string | null;
  acknowledged: boolean;
  note: string | null;
  lines: CustomerReturnLine[];
}

export interface CustomerReturnsPartner {
  id: number;
  name: string;
  commercialPartnerId: number;
  commercialPartnerName: string;
}

export interface CustomerReturnsResponse {
  status: 'success' | 'error';
  message?: string;
  partner: CustomerReturnsPartner;
  filters: { customerId: number; loadId: number | null };
  returns: CustomerReturnHeader[];
}


/** ===================== New: /delivery/load/set_state ===================== */

export interface LastPricesPayload {
  generated_at: string;     // e.g. '2025-10-09T07:12:34Z'
  user_tz: string;          // e.g. 'Asia/Kolkata'
  result: LastPriceRow[];   // ← from customer-favorites.service
}

export interface SetLoadStateRequest {
  load_id: number;
  state: UpdatableLoadState;  
}

export interface SetLoadStateResponse {
  status: 'success' | 'error';
  message?: string;
  load?: { id: number; name: string; state: LoadState, note?: string };
  changed?: boolean; // present on success
  last_prices?: LastPricesPayload;
}

/** ===================== New: /delivery/load/validate_ready_transit ===================== */

export interface ValidateReadyTransitRequest {
  load_id: number;
}

export interface ValidateReadyTransitResponse {
  status: 'success' | 'error';
  message?: string;
  ready?: boolean; // when success
  missingOrders?: string[]; // SO names
  load?: { id: number; name: string; state: LoadState, note?: string };
}

/** ===================== Mark Invoice Delivered ===================== */
export interface MarkInvoiceDeliveredRequest {
  load_id: number;
  invoice_id: number;
  delivered: boolean;
  note?: string;
}

export interface MarkInvoiceDeliveredResponse {
  status: 'success' | 'error';
  message?: string;
  link?: {
    id: number;
    loadId: number;
    invoiceId: number;
    delivered: boolean;
    deliveredOn: string | null;  // 'YYYY-MM-DD HH:mm:ss'
    deliveredBy: string | null;
    note?: string | null;
  };
  // returned when delivered=false created an auto return
  autoReturn?: DeliveryReturnSummary;
}

/** ===================== /delivery/load/force_ready_settlement ===================== */

export interface ForceReadySettlementRequest {
  load_id: number;
  /** Optional note saved on created returns (appended to return.note). */
  note?: string;
  /** Optional label used on return lines (defaults server-side to 'Undelivered (Auto-Forced)'). */
  reason_label?: string;
}

export interface ForceReadySettlementResponse {
  status: 'success' | 'error';
  message?: string;
  load?: { id: number; name: string; state: LoadState };
  summary?: {
    invoicesProcessed: number;
    returnsCreated: number;
    alreadyDeliveredCount: number;
  };
  details?: {
    processedInvoices: Array<{ id: number; name: string | null }>;
    createdReturns: Array<{ id: number; name: string; invoiceId: number }>;
    alreadyDelivered: string[]; // invoice names skipped because already delivered
  };
}

/** ===================== Models: /delivery/load/summary ===================== */

export interface LoadSummaryRequest {
  load_id: number;
}

export interface LoadSummaryRow {
  slno: number;
  partnerId: number;
  customerName: string;

  // collections (per method)
  cash: number;
  upi: number;
  cheque: number;
  bank: number;      // NEW
  other: number;     // NEW
  discount: number;  // NEW (from collections' discount field)

  // returns (qty buckets)
  returnQtyGood: number;     // NEW
  returnQtyDamaged: number;  // NEW
  returnQtyExpired: number;  // NEW
  returnQtyDue: number;      // NEW

  // legacy / compatibility (optional if you still read it elsewhere)
  returnQty?: number;        // total qty (good+damaged+expired+due), if backend keeps emitting it

  // totals unchanged
  returnAmount: number;
  customerBalance: number;
  totalAmount: number; // cash + upi + cheque (bank/other excluded by design)
}

export interface LoadSummaryTotals {
  // collections (per method)
  cash: number;
  upi: number;
  cheque: number;
  bank: number;      // NEW
  other: number;     // NEW
  discount: number;  // NEW

  // returns (qty buckets)
  returnQtyGood: number;     // NEW
  returnQtyDamaged: number;  // NEW
  returnQtyExpired: number;  // NEW
  returnQtyDue: number;      // NEW

  // legacy / compatibility (optional)
  returnQty?: number;

  // totals unchanged
  returnAmount: number;
  customerBalance: number;
  totalAmount: number; // cash + upi + cheque
}

export interface LoadSummaryResponse {
  status: 'success' | 'error';
  message?: string;
  load?: { id: number; name: string; state: LoadState, note?: string };
  rows?: LoadSummaryRow[];
  totals?: LoadSummaryTotals;
}

/** ===================== Service ===================== */

@Injectable({ providedIn: 'root' })
export class DeliveryService extends BaseApiService {
  /** Reuse the exact same DB/store scheme as CustomerFavoritesService */
  private readonly FAVORITES_DB = 'CustomerFavoritesDB';
  private storeName(customerId: number) { return `customer-${customerId}`; }
  private readonly META_DB = 'CustomerFavoritesMetaDB';
  private metaStoreName(customerId: number) { return `customer-${customerId}-meta`; }
  private readonly routeMetaStore = 'route-bulk-meta';

  constructor(
    http: HttpClient,
    busy: BusyService,
    private storage: StorageService<LastPriceRow>
  ) {
    super(http, busy);
  }

  /** POST /v1/api/delivery/loads */
  getAssignedLoads(payload: AssignedLoadsRequestPayload): Observable<AssignedLoadsResponse> {
    return this.post<AssignedLoadsResponse>('delivery/loads', payload);
  }

  /** POST /v1/api/delivery/get */
  getLoadDetails(loadId: number): Observable<LoadDetailsResponse> {
    const body: LoadDetailsRequestPayload = { load_id: loadId };
    return this.post<LoadDetailsResponse>('delivery/get', body);
  }

  /** NEW: POST /v1/api/delivery/load/set_state */
  setLoadState(loadId: number, state: UpdatableLoadState): Observable<SetLoadStateResponse> {
    const body: SetLoadStateRequest = { load_id: loadId, state };
    return this.post<SetLoadStateResponse>('delivery/load/set_state', body).pipe(
      switchMap(res => {
        const payload = res.last_prices;
        if (res.status === 'success' && payload && Array.isArray(payload.result) && payload.result.length) {
          return this.fanOutCacheFromLastPrices(payload).pipe(map(() => res));
        }
        return of(res);
      })
    );
  }

  /** NEW: POST /v1/api/delivery/load/validate_ready_transit */
  validateReadyForTransit(loadId: number): Observable<ValidateReadyTransitResponse> {
    const body: ValidateReadyTransitRequest = { load_id: loadId };
    return this.post<ValidateReadyTransitResponse>('delivery/load/validate_ready_transit', body);
  }

  /** POST /v1/api/invoice/get */
  getInvoiceById(invoiceId: number): Observable<InvoiceGetResponse> {
    return this.post<InvoiceGetResponse>('invoice/get', { invoice_id: invoiceId });
  }

  /** Return just the InvoiceDetails or throw */
  getInvoice(invoiceId: number): Observable<InvoiceDetails> {
    return this.getInvoiceById(invoiceId).pipe(
      map(res => {
        if (res.status === 'success' && res.invoice) return res.invoice;
        throw new Error(res.message || 'Failed to load invoice');
      })
    );
  }

  /** POST /v1/api/payment/create */
  createPayment(payload: PaymentRequest): Observable<PaymentResponse> {
    return this.post<PaymentResponse>('payment/create', payload);
  }

  /** POST /v1/api/return/create */
  createReturn(payload: ReturnRequest): Observable<ReturnResponse> {
    return this.post<ReturnResponse>('return/create', payload);
  }

  /** POST /v1/api/payment-return/create */
  createPaymentAndReturn(payload: PaymentAndReturnRequest): Observable<PaymentAndReturnResponse> {
    return this.post<PaymentAndReturnResponse>('payment-return/create', payload);
  }

  /** POST /v1/api/return/no-invoice */
  createStandaloneReturn(
    payload: StandaloneReturnRequest
  ): Observable<ReturnWithoutInvoiceResponse> {
    return this.post<ReturnWithoutInvoiceResponse>('return/no-invoice', payload);
  }

  /** POST /v1/api/customer/balance */
  getCustomerBalance(partnerId: number): Observable<CustomerBalanceResponse> {
    const payload: CustomerBalanceRequest = { customerId: partnerId };
    return this.post<CustomerBalanceResponse>('customer/balance', payload);
  }

  /** POST /v1/api/invoice/pending */
  listPendingInvoices(payload: PendingInvoicesRequest): Observable<PendingInvoicesResponse> {
    return this.post<PendingInvoicesResponse>('invoice/pending', payload);
  }

  /** Convenience wrapper */
  getPendingInvoicesForCustomer(
    partnerId: number,
    opts: Omit<PendingInvoicesRequest, 'partner_id'> = {}
  ): Observable<PendingInvoicesResponse> {
    return this.listPendingInvoices({ partner_id: partnerId, ...opts });
  }

  /** POST /v1/api/customer/last_invoice */
  getLastInvoiceForCustomer(partnerId: number): Observable<CustomerLastInvoiceResponse> {
    return this.post<CustomerLastInvoiceResponse>('customer/last_invoice', { customer_id: partnerId });
  }

  /** POST /v1/api/customer/credits */
  listCustomerCredits(payload: CustomerCreditsRequest): Observable<CustomerCreditsResponse> {
    return this.post<CustomerCreditsResponse>('customer/credits', payload);
  }

  /** Convenience wrapper: open credits for a customer (with optional search/pagination) */
  getOpenCreditsForCustomer(
    customerId: number,
    opts: Omit<CustomerCreditsRequest, 'customer_id'> = {}
  ): Observable<CustomerCreditsResponse> {
    const body: CustomerCreditsRequest = {
      customer_id: customerId,
      only_open: opts.only_open ?? true,
      q: opts.q,
      page: opts.page,
      limit: opts.limit,
    };
    return this.listCustomerCredits(body);
  }

  /** POST /v1/api/customer/returns */
  getCustomerReturns(payload: CustomerReturnsRequest): Observable<CustomerReturnsResponse> {
    return this.post<CustomerReturnsResponse>('customer/returns', payload);
  }

  /** POST /v1/api/delivery/invoice/mark */
  markInvoiceDelivered(markInvoiceDeliveredRequest: MarkInvoiceDeliveredRequest): Observable<MarkInvoiceDeliveredResponse> {
    return this.post<MarkInvoiceDeliveredResponse>('delivery/invoice/mark', markInvoiceDeliveredRequest);
  }

  /** POST /v1/api/delivery/load/force_ready_settlement
 *  Creates AUTO returns for all undelivered invoices on the load and forces state to ready_settlement.
 *  Does NOT toggle delivered flags on invoice links.
 */
  forceReadyForSettlement(
    loadId: number,
    opts: { note?: string; reason_label?: string } = {}
  ): Observable<ForceReadySettlementResponse> {
    const body: ForceReadySettlementRequest = {
      load_id: loadId,
      note: opts.note,
      reason_label: opts.reason_label,
    };
    return this.post<ForceReadySettlementResponse>('delivery/load/force_ready_settlement', body);
  }  

  /** POST /v1/api/delivery/load/summary */
  getLoadSummary(loadId: number): Observable<LoadSummaryResponse> {
    const body: LoadSummaryRequest = { load_id: loadId };
    return this.post<LoadSummaryResponse>('delivery/load/summary', body);
  }

  /** ===================== Private caching helpers ===================== */
  private cacheRows(customerId: number, rows: LastPriceRow[]) {
    return from(this.storage.putAll(this.FAVORITES_DB, this.storeName(customerId), rows));
  }

  private setCustomerCacheTimestamp(customerId: number, ts: number) {
    // store as single-element array for simplicity
    return from(this.storage.putAll(this.META_DB, this.metaStoreName(customerId), [{ ts }] as unknown as LastPriceRow[])).pipe(
      map(() => void 0)
    );
  }

  private setRouteBulkTimestamp(ts: number) {
    return from(this.storage.putAll(this.META_DB, this.routeMetaStore, [{ ts }] as unknown as LastPriceRow[])).pipe(
      map(() => void 0)
    );
  }

  /** Fan-out cache for payload.result (array of LastPriceRow) into per-customer stores and meta. */
  private fanOutCacheFromLastPrices(payload: LastPricesPayload) {
    const rows = payload.result || [];
    if (!rows.length) return of(void 0);

    // Group by customer
    const grouped = new Map<number, LastPriceRow[]>();
    for (const r of rows) {
      const list = grouped.get(r.customer_id) || [];
      list.push(r);
      grouped.set(r.customer_id, list);
    }

    const writes: Observable<unknown>[] = [];
    grouped.forEach((list, custId) => {
      writes.push(this.cacheRows(custId, list));
      writes.push(this.setCustomerCacheTimestamp(custId, Date.now()));
    });
    writes.push(this.setRouteBulkTimestamp(Date.now()));

    return (writes.length
      ? from(Promise.all(writes.map(o => o.toPromise()))).pipe(map(() => void 0))
      : of(void 0)
    );
  }
}
